'use client';
import React, { useState } from 'react';

import { useSearchParams } from 'next/navigation';
import MoneziteLogo from '@/components/ui/MoneziteLogo';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import { CheckCircle, TrendingUp, Users, Lightbulb } from 'lucide-react';

const BRAND_POINTS = [
  { icon: Lightbulb, text: '3,847 real-world problems posted and growing' },
  { icon: TrendingUp, text: '$142M capital deployed through the platform' },
  { icon: Users, text: '892 active investors ready to fund your solution' },
  { icon: CheckCircle, text: '634 deals successfully funded to date' },
];

export default function AuthScreen() {
  const params = useSearchParams();
  const initialMode = params?.get('mode') === 'signup' ? 'signup' : 'login';
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);

  return (
    <div className="min-h-screen flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-[44%] xl:w-[42%] gradient-primary flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background blobs */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-10 bg-white blur-3xl" />
        <div className="absolute bottom-20 left-0 w-56 h-56 rounded-full opacity-10 bg-white blur-3xl" />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <MoneziteLogo height={40} whiteText />
        </div>

        {/* Center copy */}
        <div className="relative z-10">
          <h2 className="text-3xl xl:text-4xl font-extrabold text-white leading-tight mb-4">
            Where Real Problems
            <br />
            Meet Bold Capital
          </h2>
          <p className="text-white/80 text-base leading-relaxed mb-8 max-w-sm">
            Join the platform that turns real-world challenges into funded ventures — connecting problem holders, entrepreneurs, and investors in one open ecosystem.
          </p>
          <ul className="space-y-4">
            {BRAND_POINTS?.map((point) => (
              <li key={`brand-${point?.text?.slice(0, 20)}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <point.icon size={16} className="text-white" />
                </div>
                <span className="text-white/90 text-sm font-medium">{point?.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom quote */}
        <div className="relative z-10 bg-white/10 rounded-xl p-4 border border-white/20">
          <p className="text-white/80 text-sm italic leading-relaxed">
            &quot;Monezite helped me go from a napkin sketch to a $180K seed round in 6 weeks. The investor network is unlike anything else.&quot;
          </p>
          <div className="flex items-center gap-2 mt-3">
            <div className="w-7 h-7 rounded-full bg-white/30 flex items-center justify-center">
              <span className="text-xs font-bold text-white">T</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-white">Tobias Müller</p>
              <p className="text-xs text-cyan-200">Entrepreneur, ColdRoute</p>
            </div>
          </div>
        </div>
      </div>
      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 lg:px-12 xl:px-16 bg-background overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <MoneziteLogo height={32} />
        </div>

        <div className="w-full max-w-md">
          {/* Tab switcher */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                mode === 'login' ?'bg-card text-foreground card-shadow' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Log In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                mode === 'signup' ?'bg-card text-foreground card-shadow' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign Up
            </button>
          </div>

          {mode === 'login' ? (
            <LoginForm onSwitchToSignup={() => setMode('signup')} />
          ) : (
            <SignUpForm onSwitchToLogin={() => setMode('login')} />
          )}
        </div>
      </div>
    </div>
  );
}